import type { Socket } from 'socket.io-client'
import type { ClientData, ClientToServerEvents, RoomJoinData, ServerToClientEvents, SocketData } from './types'
import { io } from 'socket.io-client'
import { ref } from 'vue'

class SocketClient {
    socket: Socket<ServerToClientEvents, ClientToServerEvents> = {} as Socket<ServerToClientEvents, ClientToServerEvents>
    url: string = ''
    debug: boolean = false
    initialized: boolean = false
    id?: string
    connectionStatus = ref<'connected' | 'disconnected' | 'connecting' | 'reconnecting'>('disconnected')
    #joinedRooms = ref(new Set<string>())

    init(namespace: string) {
        this.socket = io(`${this.debug ? 'http://localhost:3001' : 'https://blixem-websockets-2-26bbba0e2a04.herokuapp.com'}${namespace ? `/${namespace}` : ''}`, {
            autoConnect: false,
            reconnectionAttempts: 4,
            reconnectionDelay: 60000,
        })

        this.id = this.socket.id
        this.setupSocketEvents()
    }

    connect(namespace: string, auth: SocketData['user'], debug = false) {
        if (debug) { this.debug = true }

        this.init(namespace)

        if (this.debug)
            console.debug('[Sockets] connect', this.url, auth)

        this.socket.auth = auth
        this.initialized = true
        this.socket.connect()
    }

    reconnect() {
        if (!this.initialized || this.socket.connected)
            return

        if (this.debug)
            console.debug('[Sockets] reconnect')

        this.socket.connect()
    }

    disconnect() {
        if (this.debug)
            console.debug('[Sockets] disconnect')

        this.socket.auth = {}
        this.initialized = false
        this.socket.disconnect()
    }

    /**
     * Send message to room or workspace if room is empty
     */
    notify(data: Record<string, any>, room?: string) {
        if (this.debug)
            console.debug('[Sockets] notify', { room, data })

        this.socket.emit('notify', { room, data })
    }

    joinRoom<Notification extends { room: string }>(name: string, props: ConstructorParameters<typeof SocketRoom<Notification>>[1] = {}) {
        if (this.debug)
            console.debug(`[Sockets:${name}]`, 'join', props.data)

        const room = new SocketRoom<Notification>(name, props)
        this.socket.emit('join_room', { room: name, ...props.data })
        this.#joinedRooms.value.add(name)
        return room
    }

    leaveRoom(name: string) {
        if (this.debug)
            console.debug(`[Sockets:${name}]`, 'leave')

        this.socket.emit('leave_room', { room: name })
        this.#joinedRooms.value.delete(name)
        this.socket.off(`room_users_changed:${name}`)
        this.socket.off(`downstream_users_changed:${name}`)
        this.socket.off(`notify:${name}`)
    }

    get joinedRooms() {
        return this.#joinedRooms
    }

    // Socket events
    private setupSocketEvents() {
        this.socket
            .on('connect', () => {
                this.connectionStatus.value = 'connected'
                this.id = this.socket.id
            })
            .on('connect_error', (err) => {
                console.error('Socket connection error:', err, 'active:', this.socket.active)
                this.connectionStatus.value = this.socket.active ? 'reconnecting' : 'disconnected'
            })
            .on('disconnect', (reason, details) => {
                if (reason !== 'io client disconnect') {
                    console.error('Socket disconnected:', reason, details, 'active:', this.socket.active)
                }
                this.connectionStatus.value = this.socket.active ? 'reconnecting' : 'disconnected'
                this.id = this.socket.id
            })
            // @ts-expect-error unexpected property, but it's used in the code
            .on('connecting', () => {
                this.connectionStatus.value = 'connecting'
            })

        // Manager events
        this.socket.io
            .on('error', (err) => {
                console.error('Socket manager error:', err)
            })
            .on('reconnect_attempt', (attempt) => {
                console.debug('Socket reconnect attempt:', attempt)
                this.connectionStatus.value = 'reconnecting'
            })
            .on('reconnect', (attempt) => {
                console.debug('Socket reconnected:', attempt)
                this.connectionStatus.value = 'connected'
            })
    }
}

const instance = new SocketClient()
export default instance

export class SocketRoom<Notification extends { room: string }> {
    name: string
    loglabel: string = ''
    #users = ref<ClientData[]>([])
    #downstreamUsers = ref<Record<string, ClientData[]>>({})
    #callbacks: {
        message?: ((msg: Notification) => void)[]
        users?: ((users: ClientData[]) => void)[]
        downstream_users?: ((data: { rooms: Record<string, ClientData[]>, room: string }) => void)[]
    } = {}

    constructor(name: string, {
        onMessage,
        onUsersChange,
        onDownstreamUsersChange,
    }: {
        onMessage?: (msg: Notification) => void
        onUsersChange?: (users: ClientData[]) => void
        onDownstreamUsersChange?: (data: { rooms: Record<string, ClientData[]>, room: string }) => void
        data?: RoomJoinData
    } = {}) {
        this.name = name
        this.loglabel = `[Sockets:${name}]`
        if (onMessage) {
            this.on('message', onMessage)
        }
        instance.socket.on(`notify:${this.name}`, (msg) => {
            if (instance.debug)
                console.debug(this.loglabel, 'notify', msg)

            this.#fireCallbacks('message', msg as Notification)
        })

        if (onUsersChange) {
            this.on('users', onUsersChange)
        }

        instance.socket.on(`room_users_changed:${this.name}`, ({ clients }) => {
            if (JSON.stringify(this.#users.value) === JSON.stringify(clients)) {
                return
            }

            if (instance.debug)
                console.debug(this.loglabel, 'room_users_changed', clients)

            this.#users.value = clients
            this.#fireCallbacks('users', clients)
        })

        if (onDownstreamUsersChange) {
            this.on('downstream_users', onDownstreamUsersChange)
        }

        instance.socket.on(`downstream_users_changed:${this.name}`, ({ rooms, room }) => {
            if (JSON.stringify(this.#downstreamUsers.value) === JSON.stringify(rooms)) {
                return
            }
            if (instance.debug)
                console.debug(this.loglabel, 'downstream_users', rooms)

            this.#downstreamUsers.value = rooms
            this.#fireCallbacks('downstream_users', { rooms, room })
        })
    }

    leave() {
        instance.leaveRoom(this.name)
    }

    // #region Data
    get users() {
        return this.#users
    }

    get downstreamUsers() {
        return this.#downstreamUsers
    }
    // #endregion

    /**
     * Sends a message to the room, allows to override the room name if needed
     */
    notify(data: Record<string, any>, room = this.name) {
        if (instance.debug)
            console.debug(this.loglabel, 'notify', { room, data })

        instance.notify(data, room)
        return this
    }

    // #region Callbacks
    on(eventType: 'users', callback: (users: ClientData[]) => void): this
    on(eventType: 'downstream_users', callback: (data: { rooms: Record<string, ClientData[]>, room: string }) => void): this
    on(eventType: 'message', callback: (msg: Notification) => void): this
    on(eventType: 'users' | 'message' | 'downstream_users', callback: (data: any) => void) {
        if (instance.debug)
            console.debug(this.loglabel, 'addEventListener', eventType)

        if (typeof callback !== 'function')
            return this
        this.#callbacks[eventType] ??= []
        this.#callbacks[eventType]!.push(callback)
        return this
    }

    #fireCallbacks(eventType: 'users', data: ClientData[]): void
    #fireCallbacks(eventType: 'downstream_users', data: { rooms: Record<string, ClientData[]>, room: string }): void
    #fireCallbacks(eventType: 'message', data: Notification): void
    #fireCallbacks(eventType: 'users' | 'message' | 'downstream_users', data: any) {
        if (instance.debug)
            console.debug(this.loglabel, 'fireCallbacks', eventType, data)

        this.#callbacks[eventType]?.forEach(cb => cb(data))
    }
    // #endregion
}

export type Room<T extends { room: string }> = SocketRoom<T>
